import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../frontend/components/Header';
import Footer from '../frontend/components/Footer';
import { toast, ToastContainer } from 'react-toastify';
import { getAuthHeaders, authenticatedFetch } from '../utils/api';
import 'react-toastify/dist/ReactToastify.css';

const API_BASE = (process.env.REACT_APP_API_URL || 'https://localhost:7018').trim().replace(/\/$/, '');
const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || 'pk_test_xxxxxxxxxxxxxxxx';

const MyselfEnrollment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formRef = useRef(null);

  const planId = searchParams.get('planId');
  const planName = searchParams.get('planName') || 'Individual Health Plan';
  const planType = searchParams.get('planType') || 'Individual';

  const [planPrice, setPlanPrice] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paystackLoaded, setPaystackLoaded] = useState(false);

  useEffect(() => {
    if (window.PaystackPop) {
      setPaystackLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    script.onload = () => {
      console.log('✅ Paystack SDK loaded');
      setPaystackLoaded(true);
    };
    script.onerror = () => {
      console.error('❌ Failed to load Paystack SDK');
      toast.error('Failed to load payment gateway. Check your internet connection.');
    };
    document.body.appendChild(script);

    return () => {
      const existing = document.querySelector('script[src="https://js.paystack.co/v1/inline.js"]');
      if (existing) existing.remove();
    };
  }, []);

  useEffect(() => {
    const storedPlan = sessionStorage.getItem('selectedPlan');
    if (storedPlan) {
      try {
        const parsed = JSON.parse(storedPlan);
        setPlanPrice(parsed.price || 0);
      } catch (e) {
        console.error('Failed to parse stored plan', e);
      }
    }
  }, []);

  const [principalDetails, setPrincipalDetails] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    dateOfBirth: '',
    phone: '',
    email: '',
    homeAddress: '',
    state: '',
    preferredHospitalLocation: '',
    preferredHospital: ''
  });

  const [files, setFiles] = useState({ idCard: null, passportPhoto: null });
  const [fileNames, setFileNames] = useState({ idCard: '', passportPhoto: '' });

  const [stateOptions, setStateOptions] = useState([]);
  const [hospitalOptions, setHospitalOptions] = useState([]);
  const [selectedHospitalId, setSelectedHospitalId] = useState('');
  const [hospitalsLoading, setHospitalsLoading] = useState(false);

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Medical': return '🏥';
      case 'Optical': return '👁️';
      case 'Dental': return '🦷';
      default: return '🏥';
    }
  };

  const getCategorySeverity = (category) => {
    switch (category) {
      case 'Medical': return 'info';
      case 'Optical': return 'success';
      case 'Dental': return 'warning';
      default: return 'secondary';
    }
  };

  useEffect(() => {
    const loadStates = async () => {
      try {
        const result = await authenticatedFetch(`${API_BASE}/Settings/get-all-states`, 'POST', {});

        if (result?.success === true || result?.status === 'success') {
          const states = (result.data || result.result || []).map(state => ({
            label: state.name || state.Name,
            value: state.name || state.Name
          }));

          setStateOptions([{ label: 'Select state', value: '' }, ...states]);
        }
      } catch (error) {
        console.error('Failed to load states:', error);
      }
    };

    loadStates();
  }, []);

  const loadHospitalsByState = useCallback(async (stateName) => {
    if (!stateName) {
      setHospitalOptions([]);
      return;
    }

    try {
      setHospitalsLoading(true);

      const payload = stateName && typeof stateName === 'string' && stateName.trim()
        ? { state: stateName.trim() }
        : {};

      const result = await authenticatedFetch(`${API_BASE}/Settings/hospitals`, 'POST', payload);

      if (result?.success === true || result?.status === 'success') {
        const hospitals = (result.data || result.result || []).map(hospital => {
          const name = hospital.name || hospital.Name || hospital.hospitalName || 'Unknown';
          const category = hospital.category || hospital.Category || 'Medical';
          const categoryIcon = getCategoryIcon(category);

          return {
            label: `${categoryIcon} ${name} - ${category}`,
            value: hospital.id || hospital.Id,
            state: hospital.stateName || hospital.StateName,
            category,
            rawName: name
          };
        });

        setHospitalOptions(hospitals);
      } else {
        toast.error(result?.message || 'Failed to load hospitals');
        setHospitalOptions([]);
      }
    } catch (error) {
      console.error('Failed to load hospitals:', error);
      toast.error('Failed to load hospitals for selected state');
      setHospitalOptions([]);
    } finally {
      setHospitalsLoading(false);
    }
  }, []);

  const handlePrincipalChange = (e) => {
    const { name, value } = e.target;
    setPrincipalDetails(prev => ({ ...prev, [name]: value }));
  };

  const handlePreferredHospitalLocationChange = (e) => {
    const value = e.target.value;

    setPrincipalDetails(prev => ({
      ...prev,
      preferredHospitalLocation: value,
      preferredHospital: ''
    }));

    setSelectedHospitalId('');

    if (value) {
      loadHospitalsByState(value);
    } else {
      setHospitalOptions([]);
    }
  };

  const handlePreferredHospitalChange = (e) => {
    const id = e.target.value;
    setSelectedHospitalId(id);

    const hospital = hospitalOptions.find(h => h.value === id);

    setPrincipalDetails(prev => ({
      ...prev,
      preferredHospital: hospital ? hospital.rawName : ''
    }));
  };

  const handleFileUpload = (e, field) => {
    const file = e.target.files[0];

    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size exceeds 5MB limit. Please choose a smaller file.');
        e.target.value = '';
        return;
      }

      setFiles(prev => ({ ...prev, [field]: file }));
      setFileNames(prev => ({ ...prev, [field]: file.name }));
    }
  };

  const generateReference = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let ref = 'HMO-';

    for (let i = 0; i < 10; i++) {
      ref += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return ref;
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const getCleanAuthHeaders = () => {
    const authHeaders = getAuthHeaders();
    const cleanHeaders = {};

    if (!authHeaders) return cleanHeaders;

    if (typeof authHeaders.forEach === 'function') {
      authHeaders.forEach((value, key) => {
        if (key.toLowerCase() !== 'content-type') {
          cleanHeaders[key] = value;
        }
      });
    } else {
      Object.keys(authHeaders).forEach(key => {
        if (key.toLowerCase() !== 'content-type') {
          cleanHeaders[key] = authHeaders[key];
        }
      });
    }

    return cleanHeaders;
  };

  const isApiSuccess = (result) => {
    return result?.success === true || result?.status === 'success';
  };

  const openPaystackPopup = (reference, amount, email) => {
    return new Promise((resolve, reject) => {
      if (!window.PaystackPop) {
        console.error('❌ PaystackPop not available');
        return reject(new Error('Paystack not loaded'));
      }

      const amountInKobo = Math.round(amount * 100);

      const config = {
        key: PAYSTACK_PUBLIC_KEY,
        email,
        amount: amountInKobo,
        currency: 'NGN',
        ref: reference,
        meta: {
          plan_id: planId,
          plan_name: planName,
          plan_type: planType,
          applicant_name: `${principalDetails.firstName} ${principalDetails.lastName}`,
          applicant_phone: principalDetails.phone,
          preferred_hospital_location: principalDetails.preferredHospitalLocation,
          preferred_hospital: principalDetails.preferredHospital,
          preferred_hospital_id: selectedHospitalId
        },
        callback: function (response) {
          resolve(response);
        },
        onClose: function () {
          reject(new Error('Payment cancelled'));
        }
      };

      try {
        window.PaystackPop.setup(config).openIframe();
      } catch (err) {
        console.error('❌ Error opening Paystack:', err);
        reject(err);
      }
    });
  };

  const saveEnrollmentToBackend = async (paymentData) => {
    const hospitalName =
      principalDetails.preferredHospital ||
      hospitalOptions.find(h => h.value === selectedHospitalId)?.rawName ||
      '';

    const commonPayload = {
      PlanId: planId || '',
      PlanName: planName,
      PlanType: planType,
      Amount: planPrice,
      PaymentReference: paymentData.reference || paymentData.paymentReference || '',
      TransactionId: paymentData.transactionId || paymentData.tranx_id || '',
      PaymentStatus: 'success',

      FirstName: principalDetails.firstName,
      LastName: principalDetails.lastName,
      Gender: principalDetails.gender,
      DateOfBirth: principalDetails.dateOfBirth,
      Phone: principalDetails.phone,
      Email: principalDetails.email,
      State: principalDetails.state,
      HomeAddress: principalDetails.homeAddress,

      PreferredHospitalLocation: principalDetails.preferredHospitalLocation,
      PreferredHospital: hospitalName,
      PreferredHospitalId: selectedHospitalId || ''
    };

    const finalizeSuccess = () => {
      toast.success('✅ Enrollment saved successfully!');
      navigate('/success', {
        state: {
          reference: paymentData.reference || paymentData.paymentReference,
          amount: planPrice,
          planName
        }
      });
    };

    const parseResponse = async (response) => {
      const text = await response.text();

      try {
        return text ? JSON.parse(text) : {};
      } catch {
        return { raw: text };
      }
    };

    const saveAsFormData = async () => {
      const formDataObj = new FormData();

      Object.entries(commonPayload).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formDataObj.append(key, String(value));
        }
      });

      if (selectedHospitalId) {
        formDataObj.append('SelectedHospitalIds', String(selectedHospitalId));
      }

      if (files.idCard) {
        formDataObj.append('IdCardFile', files.idCard);
        formDataObj.append('IdCardFileName', files.idCard.name);
      }

      if (files.passportPhoto) {
        formDataObj.append('PassportPhotoFile', files.passportPhoto);
        formDataObj.append('PassportPhotoFileName', files.passportPhoto.name);
      }

      formDataObj.append('DependentsJson', '[]');

      const response = await fetch(`${API_BASE}/enrollment/save-after-payment`, {
        method: 'POST',
        headers: getCleanAuthHeaders(),
        body: formDataObj
      });

      if (response.status === 415) {
        return false;
      }

      const result = await parseResponse(response);

      if (response.ok && isApiSuccess(result)) {
        finalizeSuccess();
        return true;
      }

      throw new Error(result?.message || result?.title || `Server returned ${response.status}`);
    };

    const saveAsJson = async () => {
      const payload = {
        ...commonPayload,
        SelectedHospitalIds: selectedHospitalId ? [selectedHospitalId] : [],
        DependentsJson: '[]'
      };

      if (files.idCard) {
        payload.IdCardBase64 = await fileToBase64(files.idCard);
        payload.IdCardFileName = files.idCard.name;
      }

      if (files.passportPhoto) {
        payload.PassportPhotoBase64 = await fileToBase64(files.passportPhoto);
        payload.PassportPhotoFileName = files.passportPhoto.name;
      }

      const result = await authenticatedFetch(
        `${API_BASE}/enrollment/save-after-payment`,
        'POST',
        payload
      );

      if (isApiSuccess(result)) {
        finalizeSuccess();
        return true;
      }

      throw new Error(result?.message || result?.title || 'Failed to save enrollment');
    };

    try {
      const savedWithFormData = await saveAsFormData();

      if (!savedWithFormData) {
        await saveAsJson();
      }
    } catch (error) {
      console.error('💥 Save enrollment error:', error);
      toast.error(error.message || 'Payment successful but enrollment save failed. Please contact support.');
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !principalDetails.firstName ||
      !principalDetails.lastName ||
      !principalDetails.email ||
      !principalDetails.phone
    ) {
      toast.error('First name, last name, email, and phone are required');
      return;
    }

    if (!principalDetails.preferredHospitalLocation) {
      toast.error('Please select your preferred hospital location');
      return;
    }

    if (!selectedHospitalId) {
      toast.error('Please select one preferred hospital');
      return;
    }

    if (!paystackLoaded) {
      toast.warning('Payment gateway is loading. Please wait...');
      return;
    }

    if (planPrice <= 0) {
      toast.error('Invalid plan price. Please go back and select a plan.');
      return;
    }

    setIsSubmitting(true);

    try {
      const reference = generateReference();
      const paymentResponse = await openPaystackPopup(reference, planPrice, principalDetails.email);

      toast.loading('Processing payment and saving enrollment...');
      await saveEnrollmentToBackend(paymentResponse);
    } catch (error) {
      console.error('💥 Enrollment error:', error);

      if (error.message === 'Payment cancelled') {
        toast.info('Payment was cancelled. No charges were made.');
      } else {
        toast.error(error.message || 'An error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <ToastContainer position="top-right" autoClose={3000} theme="light" />

      <div style={styles.container}>
        <div style={styles.mainContent}>
          <h1 style={styles.heading}>Let's get you started in less than 24 hours.</h1>

          <div style={styles.progressContainer}>
            <div style={{ ...styles.progressBar, width: '50%' }}></div>
            <span style={styles.progressText}>50%</span>
          </div>

          <form id="enrollment-form" ref={formRef} onSubmit={handleSubmit}>
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Your Details</h3>
              <p style={styles.sectionSubtitle}>Fill in your personal details to get covered.</p>

              <div style={styles.row}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    placeholder="Enter your first name"
                    value={principalDetails.firstName}
                    onChange={handlePrincipalChange}
                    style={styles.input}
                    required
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Enter your last name"
                    value={principalDetails.lastName}
                    onChange={handlePrincipalChange}
                    style={styles.input}
                    required
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Date of Birth</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={principalDetails.dateOfBirth}
                    onChange={handlePrincipalChange}
                    style={styles.input}
                    required
                  />
                </div>
              </div>

              <div style={styles.row}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Gender</label>
                  <select
                    name="gender"
                    value={principalDetails.gender}
                    onChange={handlePrincipalChange}
                    style={styles.select}
                    required
                  >
                    <option value="">Select your gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="080XXXXXXXX"
                    value={principalDetails.phone}
                    onChange={handlePrincipalChange}
                    style={styles.input}
                    required
                  />
                </div>
              </div>

              <div style={styles.row}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="e.g., john.doe@email.com"
                    value={principalDetails.email}
                    onChange={handlePrincipalChange}
                    style={styles.input}
                    required
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>State (in Nigeria)</label>
                  <select
                    name="state"
                    value={principalDetails.state}
                    onChange={handlePrincipalChange}
                    style={styles.select}
                    required
                  >
                    <option value="">Select your state of residence</option>
                    {stateOptions.filter(s => s.value).map(state => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={styles.row}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Home Address (in Nigeria)</label>
                  <input
                    type="text"
                    name="homeAddress"
                    placeholder="e.g., 15 Admiralty Way, Lekki Phase 1"
                    value={principalDetails.homeAddress}
                    onChange={handlePrincipalChange}
                    style={styles.input}
                    required
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Preferred Hospital Location</label>
                  <select
                    name="preferredHospitalLocation"
                    value={principalDetails.preferredHospitalLocation}
                    onChange={handlePreferredHospitalLocationChange}
                    style={styles.select}
                    required
                  >
                    <option value="">Select hospital location state</option>
                    {stateOptions.filter(s => s.value).map(state => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={styles.hospitalSection}>
                <h4 style={styles.hospitalTitle}>
                  <i className="fas fa-hospital" style={{ color: '#F89423', marginRight: '8px' }}></i>
                  Preferred Hospital
                </h4>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Select One Hospital</label>
                  <select
                    value={selectedHospitalId}
                    onChange={handlePreferredHospitalChange}
                    disabled={!principalDetails.preferredHospitalLocation || hospitalsLoading}
                    style={{
                      ...styles.select,
                      opacity: (!principalDetails.preferredHospitalLocation || hospitalsLoading) ? 0.6 : 1,
                      cursor: (!principalDetails.preferredHospitalLocation || hospitalsLoading) ? 'not-allowed' : 'pointer'
                    }}
                    required
                  >
                    {hospitalsLoading ? (
                      <option disabled>Loading hospitals...</option>
                    ) : hospitalOptions.length === 0 ? (
                      <option disabled>Select a preferred hospital location first</option>
                    ) : (
                      <>
                        <option value="">Select one hospital</option>
                        {hospitalOptions.map(hospital => (
                          <option key={hospital.value} value={hospital.value}>
                            {hospital.label}
                          </option>
                        ))}
                      </>
                    )}
                  </select>

                  <small style={styles.hint}>
                    {hospitalsLoading
                      ? 'Loading hospitals...'
                      : !principalDetails.preferredHospitalLocation
                      ? 'Select preferred hospital location to see hospitals'
                      : hospitalOptions.length === 0
                      ? 'No hospitals found for this location'
                      : 'Select only one hospital'}
                  </small>
                </div>

                {selectedHospitalId && (
                  <div style={styles.selectedHospitalsContainer}>
                    <small style={styles.selectedCount}>1 hospital selected</small>

                    <div style={styles.tagsContainer}>
                      {(() => {
                        const hospital = hospitalOptions.find(h => h.value === selectedHospitalId);
                        if (!hospital) return null;

                        return (
                          <span
                            style={{
                              ...styles.tag,
                              backgroundColor: hospital.category === 'Medical' ? '#dbeafe' :
                                hospital.category === 'Optical' ? '#d1fae5' :
                                hospital.category === 'Dental' ? '#fef3c7' : '#f3f4f6',
                              color: hospital.category === 'Medical' ? '#1e40af' :
                                hospital.category === 'Optical' ? '#065f46' :
                                hospital.category === 'Dental' ? '#92400e' : '#374151'
                            }}
                          >
                            {hospital.category === 'Medical' ? '🏥' :
                              hospital.category === 'Optical' ? '👁️' :
                              hospital.category === 'Dental' ? '🦷' : '🏥'}
                            {hospital.rawName}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>

              <div style={styles.fileUploadSection}>
                <div style={styles.fileUpload}>
                  <label style={styles.fileLabel}>Identification Card (NIN, Drivers License or Passport)</label>

                  <div style={styles.fileInput}>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(e) => handleFileUpload(e, 'idCard')}
                      style={styles.hiddenFileInput}
                    />

                    <div
                      style={{
                        ...styles.filePlaceholder,
                        borderColor: fileNames.idCard ? '#F89423' : '#d1d5db',
                        backgroundColor: fileNames.idCard ? '#fff7ed' : '#f9fafb'
                      }}
                    >
                      {fileNames.idCard ? (
                        <>
                          <i className="fas fa-check-circle" style={{ ...styles.uploadIcon, color: '#10b981' }}></i>
                          <p style={{ color: '#1f2937', fontWeight: '600', margin: '8px 0 0 0' }}>
                            {fileNames.idCard}
                          </p>
                          <small style={{ color: '#6b7280' }}>Click to change file</small>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-upload" style={styles.uploadIcon}></i>
                          <p style={{ margin: '8px 0 0 0' }}>Choose a file to upload</p>
                          <small>JPEG, PNG or PDF • Maximum file size: 5MB</small>
                          <small style={styles.secureText}>🔒 Secure & encrypted upload</small>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div style={styles.fileUpload}>
                  <label style={styles.fileLabel}>Passport Photograph</label>

                  <div style={styles.fileInput}>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      onChange={(e) => handleFileUpload(e, 'passportPhoto')}
                      style={styles.hiddenFileInput}
                    />

                    <div
                      style={{
                        ...styles.filePlaceholder,
                        borderColor: fileNames.passportPhoto ? '#F89423' : '#d1d5db',
                        backgroundColor: fileNames.passportPhoto ? '#fff7ed' : '#f9fafb'
                      }}
                    >
                      {fileNames.passportPhoto ? (
                        <>
                          <i className="fas fa-check-circle" style={{ ...styles.uploadIcon, color: '#10b981' }}></i>
                          <p style={{ color: '#1f2937', fontWeight: '600', margin: '8px 0 0 0' }}>
                            {fileNames.passportPhoto}
                          </p>
                          <small style={{ color: '#6b7280' }}>Click to change file</small>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-upload" style={styles.uploadIcon}></i>
                          <p style={{ margin: '8px 0 0 0' }}>Choose a file to upload</p>
                          <small>JPEG, PNG or JPG • Maximum file size: 5MB</small>
                          <small style={styles.secureText}>🔒 Secure & encrypted upload</small>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.navigation}>
              <button
                type="button"
                style={styles.prevButton}
                onClick={() => navigate(-1)}
                disabled={isSubmitting}
              >
                <i className="fas fa-arrow-left"></i> Previous
              </button>

              <button
                type="submit"
                style={{
                  ...styles.continueButton,
                  opacity: isSubmitting ? 0.7 : 1,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer'
                }}
                disabled={isSubmitting || !paystackLoaded}
              >
                {isSubmitting ? 'Processing...' : !paystackLoaded ? 'Loading Payment...' : 'Proceed to Payment'}{' '}
                <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          </form>
        </div>

        <div style={styles.sidebar}>
          <div style={styles.orderSummary}>
            <h3 style={styles.summaryTitle}>Order Summary</h3>
            <p style={styles.summarySubtitle}>Review your plan details and pricing</p>

            <div style={styles.summaryCard}>
              <div style={styles.summaryRow}>
                <span style={styles.summaryLabel}>Plan</span>
                <span style={styles.summaryValue}>{planName}</span>
              </div>

              <div style={styles.summaryRow}>
                <span style={styles.summaryLabel}>Purchase Type</span>
                <span style={{ ...styles.purchaseType, backgroundColor: '#FFF3E0', color: '#F89423' }}>
                  {planType.toUpperCase()}
                </span>
              </div>

              <div style={styles.summaryRow}>
                <span style={styles.summaryLabel}>Preferred Hospital Location</span>
                <span style={styles.summaryValue}>
                  {principalDetails.preferredHospitalLocation || 'Not selected'}
                </span>
              </div>

              <div style={styles.summaryRow}>
                <span style={styles.summaryLabel}>Preferred Hospital</span>
                <span style={styles.summaryValue}>
                  {principalDetails.preferredHospital || 'Not selected'}
                </span>
              </div>
            </div>

            <div style={styles.pricingSection}>
              <h4 style={styles.pricingTitle}>Pricing Breakdown</h4>

              <div style={styles.pricingRow}>
                <span>Principal (You)</span>
                <span style={styles.price}>₦{Number(planPrice).toLocaleString()}</span>
              </div>

              <div style={styles.totalRow}>
                <span style={styles.totalLabel}>Total Amount</span>
                <span style={{ ...styles.totalAmount, color: '#F89423' }}>
                  ₦{Number(planPrice).toLocaleString()}
                </span>
              </div>
            </div>

            <button
              type="button"
              form="enrollment-form"
              style={{
                ...styles.paymentButton,
                borderColor: '#F89423',
                color: '#F89423',
                opacity: (isSubmitting || !paystackLoaded) ? 0.7 : 1,
                cursor: (isSubmitting || !paystackLoaded) ? 'not-allowed' : 'pointer'
              }}
              disabled={isSubmitting || !paystackLoaded}
              onClick={() => formRef.current?.requestSubmit()}
            >
              {isSubmitting ? 'Processing...' : !paystackLoaded ? 'Loading Payment...' : 'Make Payment'}{' '}
              <i className="fas fa-arrow-right"></i>
            </button>

            <small
              style={{
                display: 'block',
                textAlign: 'center',
                marginTop: '12px',
                color: '#6b7280',
                fontSize: '12px'
              }}
            >
              <i className="fas fa-lock" style={{ marginRight: '4px' }}></i>
              Secure payment via Paystack
            </small>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

const styles = {
  container: { display: 'flex', maxWidth: '1200px', margin: '0 auto', padding: '40px 20px', gap: '40px' },
  mainContent: { flex: 1, maxWidth: '800px' },
  heading: { fontSize: '28px', fontWeight: '600', marginBottom: '30px', color: '#1a1a1a' },
  progressContainer: { position: 'relative', height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', marginBottom: '40px' },
  progressBar: { height: '100%', backgroundColor: '#F89423', borderRadius: '4px', transition: 'width 0.3s ease' },
  progressText: { position: 'absolute', right: '0', top: '-25px', fontSize: '14px', color: '#F89423', fontWeight: '600' },
  section: { marginBottom: '40px' },
  sectionTitle: { fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#1a1a1a' },
  sectionSubtitle: { fontSize: '14px', color: '#6b7280', marginBottom: '24px' },
  row: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '20px' },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' },
  label: { fontSize: '14px', fontWeight: '500', color: '#374151' },
  input: { padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s' },
  select: { padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', outline: 'none', backgroundColor: 'white', cursor: 'pointer' },
  hint: { fontSize: '12px', color: '#6b7280', marginTop: '4px' },

  hospitalSection: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '20px',
    marginTop: '20px'
  },
  hospitalTitle: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '16px',
    color: '#1a1a1a',
    display: 'flex',
    alignItems: 'center'
  },
  selectedHospitalsContainer: {
    marginTop: '12px'
  },
  selectedCount: {
    fontSize: '12px',
    color: '#6b7280',
    display: 'block',
    marginBottom: '8px'
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '16px',
    fontSize: '13px',
    fontWeight: '500',
    border: '1px solid'
  },

  fileUploadSection: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginTop: '20px' },
  fileUpload: { display: 'flex', flexDirection: 'column', gap: '8px' },
  fileLabel: { fontSize: '14px', fontWeight: '500', color: '#374151' },
  fileInput: { position: 'relative' },
  hiddenFileInput: { position: 'absolute', width: '100%', height: '100%', opacity: '0', cursor: 'pointer', zIndex: 2 },
  filePlaceholder: { border: '2px dashed #d1d5db', borderRadius: '8px', padding: '24px', textAlign: 'center', backgroundColor: '#f9fafb', cursor: 'pointer', transition: 'all 0.2s', minHeight: '150px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  uploadIcon: { fontSize: '24px', color: '#F89423', marginBottom: '8px' },
  secureText: { color: '#059669', fontSize: '11px', marginTop: '4px' },

  navigation: { display: 'flex', justifyContent: 'space-between', marginTop: '40px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' },
  prevButton: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 24px', border: 'none', backgroundColor: 'transparent', color: '#6b7280', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  continueButton: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 24px', border: 'none', borderRadius: '6px', backgroundColor: '#F89423', color: 'white', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },

  sidebar: { width: '350px', position: 'sticky', top: '100px', height: 'fit-content' },
  orderSummary: { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' },
  summaryTitle: { fontSize: '16px', fontWeight: '600', marginBottom: '4px', color: '#1a1a1a' },
  summarySubtitle: { fontSize: '13px', color: '#6b7280', marginBottom: '20px' },
  summaryCard: { backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px', marginBottom: '20px' },
  summaryRow: { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' },
  summaryLabel: { fontSize: '12px', color: '#6b7280' },
  summaryValue: { fontSize: '14px', fontWeight: '500', color: '#1a1a1a' },
  purchaseType: { display: 'inline-block', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '600', marginTop: '4px' },

  pricingSection: { marginBottom: '20px' },
  pricingTitle: { fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#1a1a1a' },
  pricingRow: { display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#6b7280', marginBottom: '8px' },
  price: { fontWeight: '500', color: '#1a1a1a' },
  totalRow: { display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid #e5e7eb', marginTop: '12px' },
  totalLabel: { fontSize: '14px', fontWeight: '600', color: '#1a1a1a' },
  totalAmount: { fontSize: '18px', fontWeight: '700' },

  paymentButton: { width: '100%', padding: '14px', border: '2px solid', borderRadius: '8px', backgroundColor: 'white', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.2s' }
};

export default MyselfEnrollment;